import { Size, cellOn, columnAllOn, rowAllOn, t, toggleCell, toggleColumn, toggleRow, type PermissionMatrix } from '@bms2/shared';
import { Checkbox } from '../controls';
import styles from './shared.module.css';

/**
 * Permission Grid (piece 4): rows, columns and a boolean cell, for the role
 * editor's module × read/write/edit/delete grid and decision 0068's role ×
 * document class grid. It is a table of native checkboxes (decision 0070);
 * a whole row and a whole column toggle in one action.
 */
export const permissionGridDocumentedStates = ['empty', 'partial', 'full'] as const;

export interface GridAxis {
  key: string;
  label: string;
}

export interface PermissionGridProps {
  rows: readonly GridAxis[];
  columns: readonly GridAxis[];
  matrix: PermissionMatrix;
  onChange: (next: PermissionMatrix) => void;
  /** The heading over the row labels, such as Module. */
  rowHeading: string;
  caption: string;
}

function rowToggleLabel(row: string): string {
  return `${t('grid.toggleRow')}: ${row}`;
}

function columnToggleLabel(column: string): string {
  return `${t('grid.toggleColumn')}: ${column}`;
}

function cellLabel(row: string, column: string): string {
  return `${row}: ${column}`;
}

export function PermissionGrid({ rows, columns, matrix, onChange, rowHeading, caption }: PermissionGridProps) {
  const rowKeys = rows.map((r) => r.key);
  const colKeys = columns.map((c) => c.key);
  return (
    <div className={styles.gridWrap}>
      <table className={styles.grid} data-permission-grid="true">
        <caption className={styles.gridCaption}>{caption}</caption>
        <thead>
          <tr>
            <th scope="col">{rowHeading}</th>
            {columns.map((c) => (
              <th key={c.key} scope="col" data-toggle-column={c.key}>
                <span className={styles.gridHead}>
                  <Checkbox
                    label={columnToggleLabel(c.label)}
                    labelHidden
                    size={Size.Small}
                    checked={columnAllOn(matrix, rowKeys, c.key)}
                    onChange={() => onChange(toggleColumn(matrix, rowKeys, c.key))}
                  />
                  <span>{c.label}</span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <th scope="row" data-toggle-row={r.key}>
                <span className={styles.gridHead}>
                  <Checkbox label={rowToggleLabel(r.label)} labelHidden size={Size.Small} checked={rowAllOn(matrix, r.key, colKeys)} onChange={() => onChange(toggleRow(matrix, r.key, colKeys))} />
                  <span>{r.label}</span>
                </span>
              </th>
              {columns.map((c) => (
                <td key={c.key} className={styles.gridCell} data-cell={`${r.key}:${c.key}`}>
                  <Checkbox label={cellLabel(r.label, c.label)} labelHidden size={Size.Small} checked={cellOn(matrix, r.key, c.key)} onChange={() => onChange(toggleCell(matrix, r.key, c.key))} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
